// Chakra imports
import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useUserContext } from "userContextProvider/UserRoleContext";
// Custom components
import Card from "components/card/Card.js";
import LineChart from "components/charts/LineChart";
import React from "react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { MdBarChart, MdOutlineCalendarToday } from "react-icons/md";
// Assets
import { RiArrowUpSFill, RiArrowDownSFill } from "react-icons/ri";
import {
  lineChartDataTotalSpent,
  lineChartOptionsTotalSpent,
} from "variables/charts";
import getSymbolFromCurrency from 'currency-symbol-map';

export default function TotalSpent(props) {
  const { userData } = useUserContext();
  const monthlyEarnings = userData.monthlyEarnings || 0;
  const previousMonthlyEarnings = userData.previousMonthlyEarnings || 0;
  const { ...rest } = props;

  let earningsChangePercent = 0;
  let isPositive = true;

  if (previousMonthlyEarnings === 0 && monthlyEarnings === 0) {
    earningsChangePercent = 0;
    isPositive = true; // or false, depending on your preference
  } else if (previousMonthlyEarnings === 0) {
    earningsChangePercent = 100;
    isPositive = true;
  } else {
    const difference = monthlyEarnings - previousMonthlyEarnings;
    earningsChangePercent = ((difference / previousMonthlyEarnings) * 100).toFixed(2);
    isPositive = earningsChangePercent >= 0;
  }



  const formatCurrencyShort = (value, currencyCode = "USD", locale = "en-US") => {
    if (value === null || value === undefined) {
      return getSymbolFromCurrency(currencyCode) + "0";
    }

    const num = Number(value);
    const suffixes = ["", "K", "M", "B", "T"];
    let suffixIndex = 0;

    let shortValue = num;

    while (shortValue >= 1000 && suffixIndex < suffixes.length - 1) {
      shortValue /= 1000;
      suffixIndex++;
    }

    // Format the number to match locale rules (e.g., commas)
    let formattedNumber;

    try {
      formattedNumber = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(shortValue);
    } catch (error) {
      formattedNumber = shortValue.toFixed(2);
    }

    const symbol = getSymbolFromCurrency(currencyCode);

    return `${symbol}${formattedNumber}${suffixes[suffixIndex]}`;
  };

  // Chakra Color Mode

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const iconColor = useColorModeValue("brand.500", "white");
  const bgButton = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const bgHover = useColorModeValue(
    { bg: "secondaryGray.400" },
    { bg: "whiteAlpha.50" }
  );
  const bgFocus = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.100" }
  );
  return (
    <Card
      justifyContent='center'
      align='center'
      direction='column'
      w='100%'
      mb='0px'
      {...rest}>
      <Flex justify='space-between' ps='0px' pe='20px' pt='5px'>
        <Flex align='center' w='100%'>
          <Button
            bg={boxBg}
            fontSize='sm'
            fontWeight='500'
            color={textColorSecondary}
            borderRadius='7px'>
            <Icon
              as={MdOutlineCalendarToday}
              color={textColorSecondary}
              me='4px'
            />
            This month
          </Button>
          <Button
            ms='auto'
            align='center'
            justifyContent='center'
            bg={bgButton}
            _hover={bgHover}
            _focus={bgFocus}
            _active={bgFocus}
            w='37px'
            h='37px'
            lineHeight='100%'
            borderRadius='10px'
            {...rest}>
            <Icon as={MdBarChart} color={iconColor} w='24px' h='24px' />
          </Button>
        </Flex>
      </Flex>
      <Flex w='100%' flexDirection={{ base: "column", lg: "row" }}>
        <Flex flexDirection='column' me='20px' mt='28px'>
          <Text
            color={textColor}
            fontSize='34px'
            textAlign='start'
            fontWeight='700'
            lineHeight='100%'>
            {formatCurrencyShort(userData?.monthlyEarnings, userData?.currencySymbol)}
          </Text>
          <Flex align='center' mb='20px'>
            <Text
              color='secondaryGray.600'
              fontSize='sm'
              fontWeight='500'
              mt='4px'
              me='12px'>
              Total Earnings
            </Text>
            <Flex align="center">
              <Icon
                as={isPositive ? RiArrowUpSFill : RiArrowDownSFill}
                color={isPositive ? "green.500" : "red.500"}
                me="2px"
                mt="2px"
              />
              <Text color={isPositive ? "green.500" : "red.500"} fontSize="sm" fontWeight="700">
                {isPositive ? "+" : ""}
                {earningsChangePercent}%
              </Text>
            </Flex>

          </Flex>

          <Flex align='center'>
            <Icon as={IoCheckmarkCircle} color='green.500' me='4px' />
            <Text color='green.500' fontSize='md' fontWeight='700'>
              On track
            </Text>
          </Flex>
        </Flex>
        {/* <Box minH='260px' minW='75%' mt='auto'>
          <LineChart
            chartData={lineChartDataTotalSpent}
            chartOptions={lineChartOptionsTotalSpent}
          />
        </Box> */}
      </Flex>
    </Card>
  );
}
